// @ts-nocheck
/* eslint-disable */
import React, { useMemo, useRef } from 'react'
import type { i18n as I18NextClient, Resource } from 'i18next'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { I18nextProvider } from 'react-i18next'
import type { AppProps as NextJsAppProps } from 'next/app'
import { getUserLocale, setUserLocale, setAcceptLocale } from '@/utils'
import { createConfig } from './config/createConfig'
import createClient from './createClient'
import { useIsomorphicLayoutEffect } from './utils'
import type { SSRConfig, UserConfig } from './types'

export { Trans, useTranslation, withTranslation } from 'react-i18next'

export let globalI18n: I18NextClient | null = null

const addResourcesToI18next = (instance: I18NextClient, resources: Resource) => {
  if (resources && instance.isInitialized) {
    for (const locale of Object.keys(resources)) {
      for (const ns of Object.keys(resources[locale])) {
        if (
          !instance?.store?.data ||
          !instance.store.data[locale] ||
          !instance.store.data[locale][ns]
        ) {
          instance.addResourceBundle(locale, ns, resources[locale][ns], true, true)
        }
      }
    }
  }
}

export const appWithTranslation = <Props extends NextJsAppProps>(
  WrappedComponent: React.ComponentType<Props>,
  configOverride: UserConfig | null = null,
) => {
  const AppWithTranslation = (props: Props & { pageProps: Props['pageProps'] & SSRConfig }) => {
    const { _nextI18Next } = props.pageProps || {}
    let locale: string | undefined = _nextI18Next?.initialLocale ?? props?.router?.locale
    const acceptLocale = _nextI18Next?.acceptLocale
    const ns = _nextI18Next?.ns
    // console.log(props, _nextI18Next, '====>_nextI18Next')
    // console.log(locale, '====>locale')
    // console.log(acceptLocale, '====>acceptLocale')
    const instanceRef = useRef<I18NextClient | null>(null)

    const i18n: I18NextClient | null = useMemo(() => {
      if (!_nextI18Next && !configOverride) return null

      const userConfig = configOverride ?? _nextI18Next?.userConfig

      if (!userConfig) {
        throw new Error('appWithTranslation was called without a next-lang config')
      }

      if (!userConfig?.i18n) {
        throw new Error('appWithTranslation was called without config.i18n')
      }

      if (!userConfig?.i18n?.defaultLocale) {
        throw new Error('config.i18n does not include a defaultLocale property')
      }

      const { initialI18nStore } = _nextI18Next || {}
      const resources = configOverride?.resources ? configOverride.resources : initialI18nStore

      if (!locale) locale = userConfig.i18n.defaultLocale

      let instance = instanceRef.current
      if (instance) {
        addResourcesToI18next(instance, resources)
      } else {
        instance = createClient({
          ...createConfig({
            ...userConfig,
            lng: locale,
          }),
          lng: locale,
          ...(ns && { ns }),
          resources,
          debug: false,
        }).i18n

        addResourcesToI18next(instance, resources)

        globalI18n = instance
        instanceRef.current = instance
      }

      return instance
    }, [_nextI18Next, locale, ns])

    useIsomorphicLayoutEffect(() => {
      if (acceptLocale) {
        setAcceptLocale(acceptLocale)
      }
    }, [acceptLocale])

    useIsomorphicLayoutEffect(() => {
      if (!i18n || !locale) return
      if (locale && getUserLocale() !== locale) {
        // 更新用户语言
        setUserLocale(locale)
      }
      i18n.changeLanguage(locale)
    }, [i18n, locale])

    return i18n !== null ? (
      <I18nextProvider i18n={i18n}>
        <WrappedComponent {...props} />
      </I18nextProvider>
    ) : (
      <WrappedComponent key={locale} {...props} />
    )
  }

  return hoistNonReactStatics(AppWithTranslation, WrappedComponent)
}
